import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

/**
 * The fingerprint or the face, as the way back into the application.
 *
 * What it actually guards is the session token in `host.ts`. The application
 * already keeps that token in its private storage, so it does not have to ask
 * for a password every morning; this puts the phone's own lock in front of it,
 * which is what makes keeping it there reasonable in the first place.
 *
 * It is deliberately *not* a second factor. The server never sees it and nothing
 * about it proves anything to anybody — it decides whether this application, on
 * this phone, will use a credential it already holds. A stolen phone with the
 * lock on is a phone the thief has to get past Android with.
 *
 * Off until switched on, from the account screen. Turning it on for people is
 * how a workshop phone that half the room shares ends up locked to one finger.
 *
 * Everything loads on demand: the browser build never carries the plugin, and
 * never asks a question that has no meaning in a browser.
 */

const LOCK_KEY = 'storageapp.biometricLock';

const native = Capacitor.isNativePlatform();

/**
 * What this phone says about its own fingerprint reader.
 *
 * `reason` is the phone's own words for why it is not available — nothing
 * enrolled, hardware busy, no hardware at all. It is carried out of here
 * because the alternative, which is what this used to do, is to hide the whole
 * section whenever the answer is no: and then "this phone has no fingerprint
 * registered" and "the plugin failed to load" look exactly alike from the
 * outside, which is a screen that cannot be diagnosed by the person looking
 * at it.
 */
export interface Biometry {
  available: boolean;
  /** Absent in a browser, where the question does not arise. */
  reason?: string;
}

export async function checkBiometry(): Promise<Biometry> {
  if (!native) return { available: false };
  try {
    const { BiometricAuth } = await import('@aparajita/capacitor-biometric-auth');
    const info = await BiometricAuth.checkBiometry();
    return { available: info.isAvailable, reason: info.reason || undefined };
  } catch (error) {
    return { available: false, reason: (error as Error)?.message || 'plugin' };
  }
}

/** Whether this phone has a fingerprint or a face registered at all. */
export async function available(): Promise<boolean> {
  return (await checkBiometry()).available;
}

export async function lockEnabled(): Promise<boolean> {
  if (!native) return false;
  try {
    return (await Preferences.get({ key: LOCK_KEY })).value === 'on';
  } catch {
    return false;
  }
}

export async function setLockEnabled(on: boolean): Promise<void> {
  if (!native) return;
  try {
    if (on) await Preferences.set({ key: LOCK_KEY, value: 'on' });
    else await Preferences.remove({ key: LOCK_KEY });
  } catch {
    // A lock that could not be recorded is a lock that is off. Failing the
    // other way would leave somebody unable to get in.
  }
}

/**
 * Ask, and say whether the phone was satisfied.
 *
 * `allowDeviceCredential` on purpose: a finger that will not read on a cold
 * morning must not lock somebody out of their own warehouse, and the PIN that
 * unlocks the phone is the same thing the fingerprint stands for.
 */
export async function verify(reason: string): Promise<boolean> {
  if (!native) return true;
  try {
    const { BiometricAuth } = await import('@aparajita/capacitor-biometric-auth');
    await BiometricAuth.authenticate({
      reason,
      androidTitle: reason,
      allowDeviceCredential: true,
      androidConfirmationRequired: false,
    });
    return true;
  } catch {
    // Cancelled, or not recognised. Both mean the same thing here.
    return false;
  }
}
